import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  Box,
  Paper,
  Skeleton,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  RemoveCircleOutline as RemoveIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const FolderDocumentsTable = ({ 
  documents, 
  loading, 
  onView, 
  onDownload, 
  onRemove, 
  canManage 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'PENDING': return 'warning';
      case 'EXPIRED': return 'error';
      case 'REVIEW': return 'info';
      case 'NOT_APPROVED': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Added</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3].map(i => (
              <TableRow key={i}>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <DocumentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          No documents in this folder
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add documents from the List view
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Added</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((doc) => (
            <TableRow key={doc.id} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <DocumentIcon sx={{ mr: 1, color: 'action.active' }} fontSize="small" />
                  <Typography variant="body2">
                    {doc.name}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {doc.documentType || doc.type || 'Document'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip 
                  label={doc.status || 'UNKNOWN'} 
                  size="small" 
                  color={getStatusColor(doc.status)}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {doc.addedAt ? format(new Date(doc.addedAt), 'MMM dd, yyyy') : '-'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="View Document">
                  <IconButton 
                    size="small" 
                    onClick={() => onView(doc)}
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton 
                    size="small"
                    onClick={() => onDownload(doc)}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canManage && (
                  <Tooltip title="Remove from Folder">
                    <IconButton 
                      size="small" 
                      onClick={() => onRemove(doc)}
                      color="error"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FolderDocumentsTable;